import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, signInWithRedirect, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import logger from '../services/logger';
import OAuthButton from './OAuthButton';
import Header from './home/Header';
import AuthHero from './auth/AuthHero';
import Logo from './Logo';
import {oauthEnabled} from "../aws-config";

function Login() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSupportMessage, setShowSupportMessage] = useState(false);
  const [showWaitlistMessage, setShowWaitlistMessage] = useState(false);
  const [retryAttempted, setRetryAttempted] = useState(false);

  useEffect(() => {
    // Check for waitlist logout message
    if (localStorage.getItem('waitlist_logout_message')) {
      setShowWaitlistMessage(true);
      localStorage.removeItem('waitlist_logout_message');
    }
    
    if (user) {
      // Check for redirect loop from Plans page
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('verification_issue') === 'true') {
        setShowSupportMessage(true);
        return;
      }
      navigate('/plans');
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      setError('');
      setShowConfirmation(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const signInWith = async (provider) => {
    try {
      setLoading(true);
      await signInWithRedirect({ provider });
    } catch (error) {
      console.error('OAuth error:', error);
      if (error.name === 'UserAlreadyAuthenticatedException') {
        setError('You are already signed in. Please logout first or refresh the page.');
      } else {
        setError(`${provider} login failed: ${error.message}`);
      }
      setLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setRetryAttempted(false);
    
    try {
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      // Check if sign in actually succeeded
      if (!result?.isSignedIn) {
        // This indicates an unconfirmed user - signIn "succeeds" but doesn't actually sign in
        setShowConfirmation(true);
        setError('Your account needs to be confirmed. Please check your email and enter the confirmation code.');
        return;
      }
      
      // Check if user is waitlist user before proceeding
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        const userType = payload['custom:user_type'];
        
        // If waitlist user, clear auth and show message
        if (userType === 'waitlist' || groups.includes('waitlist')) {
          localStorage.clear();
          sessionStorage.clear();
          setShowWaitlistMessage(true);
          return;
        }
      }
      
      // Log user login after successful sign in (only for approved users)
      await logger.logUserLogin(result);
      
      // Wait for auth state to update, then navigate
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check for stored redirect URL
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        // Check subscription status to determine redirect
        try {
          const { configService } = await import('../services/configService');
          const status = await configService.getSubscriptionStatus();
          if (status?.status === 'active' || status?.status === 'trialing') {
            navigate('/dashboard');
          } else {
            navigate('/plans');
          }
        } catch (error) {
          // If subscription check fails, default to plans
          navigate('/plans');
        }
      }
    } catch (error) {
      // Check if user needs email confirmation
      if (error.message.includes('User is not confirmed') || 
          error.name === 'UserNotConfirmedException' ||
          (error.name === 'BadRequestException' && error.message.includes('400')) ||
          error.message.includes('UNCONFIRMED') ||
          error.message.includes('confirm')) {
        setShowConfirmation(true);
        setError('Your account needs to be confirmed. Please check your email and enter the confirmation code.');
      } else if (error.name === 'UserAlreadyAuthenticatedException' && !retryAttempted) {
        setRetryAttempted(true);
        try {
          // Force logout to clear stale state
          await logout();
          // Clear any localStorage tokens
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
              localStorage.removeItem(key);
            }
          });
          
          // Wait for cleanup to complete, then automatically retry
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const retryResult = await signIn({
            username: formData.email,
            password: formData.password
          });
          
          if (retryResult?.isSignedIn) {
            // Check if user is waitlist user before proceeding
            const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
            const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
            const idToken = localStorage.getItem(idTokenKey);
            
            if (idToken) {
              const payload = JSON.parse(atob(idToken.split('.')[1]));
              const groups = payload['cognito:groups'] || [];
              const userType = payload['custom:user_type'];
              
              // If waitlist user, clear auth and show message
              if (userType === 'waitlist' || groups.includes('waitlist')) {
                localStorage.clear();
                sessionStorage.clear();
                setShowWaitlistMessage(true);
                return;
              }
            }
            
            await logger.logUserLogin(retryResult);
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Check for stored redirect URL
            const redirectUrl = localStorage.getItem('redirectAfterLogin');
            if (redirectUrl) {
              localStorage.removeItem('redirectAfterLogin');
              navigate(redirectUrl);
            } else {
              // Check subscription status to determine redirect
              try {
                const { configService } = await import('../services/configService');
                const status = await configService.getSubscriptionStatus();
                if (status?.status === 'active' || status?.status === 'trialing') {
                  navigate('/dashboard');
                } else {
                  navigate('/plans');
                }
              } catch (error) {
                // If subscription check fails, default to plans
                navigate('/plans');
              }
            }
            return; // Exit successfully
          }
        } catch (retryError) {
          console.error('Auto-retry failed:', retryError);
          if (retryError.message.includes('User is not confirmed') || 
              retryError.name === 'UserNotConfirmedException') {
            setShowConfirmation(true);
            setError('Your account needs to be confirmed. Please check your email and enter the confirmation code.');
            return;
          }
          setError('Session cleared but login retry failed. Please try again.');
        }
      } else if (error.name === 'UserAlreadyAuthenticatedException' && retryAttempted) {
        setError('Authentication conflict persists. Please refresh the page and try again.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmation = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode
      });
      
      // Automatically sign in after confirmation
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      // Check if user is waitlist user before logging
      const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        const userType = payload['custom:user_type'];
        
        // If waitlist user, clear auth and redirect
        if (userType === 'waitlist' || groups.includes('waitlist')) {
          localStorage.clear();
          sessionStorage.clear();
          localStorage.setItem('waitlist_logout_message', 'true');
          window.location.href = '/login';
          return;
        }
      }
      
      // Log user login after successful confirmation and sign in (only for approved users)
      await logger.logUserLogin(result);
      
      // Check for stored redirect URL
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        // Check subscription status to determine redirect
        try {
          const { configService } = await import('../services/configService');
          const status = await configService.getSubscriptionStatus();
          if (status?.status === 'active' || status?.status === 'trialing') {
            navigate('/dashboard');
          } else {
            navigate('/plans');
          }
        } catch (error) {
          // If subscription check fails, default to plans
          navigate('/plans');
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-1">
        <AuthHero />

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
        <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Welcome Back</h2>
          <p className="text-gray-600 mb-6 text-sm font-['Inter']">Sign in to your account</p>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm border border-red-200">
              {error}
            </div>
          )}

          {showWaitlistMessage && (
            <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg mb-4 text-sm border border-yellow-200">
              <h3 className="font-semibold mb-1">Account Pending Approval</h3>
              <p className="mb-2">Your account is on the waitlist and requires approval before you can access the platform.</p>
              <p className="text-xs">Please contact support at <a href="mailto:support@remote-assistant.ai" className="text-blue-600 hover:underline">support@remote-assistant.ai</a> for assistance.</p>
            </div>
          )}

          {showSupportMessage && (
            <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg mb-4 text-sm border border-yellow-200">
              <h3 className="font-semibold mb-1">Account Verification Issue</h3>
              <p className="mb-2">Your account appears to have a verification issue that requires manual assistance.</p>
              <p className="text-xs">Please contact support at <a href="mailto:support@remote-assistant.ai" className="text-blue-600 hover:underline">support@remote-assistant.ai</a> for help resolving this issue.</p>
            </div>
          )}

          {oauthEnabled && (
            <>
              <div className="mb-6 space-y-2">
                <OAuthButton 
                  provider="google"
                  onClick={() => signInWith('Google')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <OAuthButton 
                  provider="facebook"
                  onClick={() => signInWith('Facebook')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <OAuthButton 
                  provider="apple"
                  onClick={() => signInWith('SignInWithApple')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <OAuthButton 
                  provider="microsoft"
                  onClick={() => signInWith('LoginWithAmazon')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
              </div>

              <div className="my-6 text-gray-500 relative text-sm text-center">
                <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-200"></div>
                <span className="bg-white px-4 relative">or</span>
              </div>
            </>
          )}

          {!showConfirmation ? (
            <form onSubmit={handleLogin} className="flex flex-col gap-3">
              <input 
                type="email" 
                name="email"
                placeholder="Email Address" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="relative">
                <input 
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password" 
                  value={formData.password}
                  onChange={handleInputChange}
                  required 
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary text-sm py-3 ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
              <Link to="/reset-password" className="text-xs text-blue-600 hover:text-blue-700 no-underline text-center mt-1">
                Forgot password?
              </Link>
            </form>
          ) : (
            <form onSubmit={handleConfirmation} className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 font-['Inter']">Check Your Email</h2>
              <p className="text-gray-600 mb-3 text-sm">
                We sent a confirmation code to {formData.email}
              </p>
              <input 
                type="text" 
                placeholder="Enter confirmation code" 
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required 
                className="p-3 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary text-sm py-3 ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>
              <div className="flex flex-col gap-2 mt-3">
                <button 
                  type="button" 
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await resendSignUpCode({ username: formData.email });
                      setError('Confirmation code resent to your email.');
                    } catch (error) {
                      setError('Failed to resend code: ' + error.message);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer text-xs"
                >
                  Resend confirmation code
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowConfirmation(false)}
                  className="text-gray-600 hover:text-gray-800 bg-transparent border-none cursor-pointer text-xs"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}

          {!showConfirmation && (
            <>
              <p className="mt-4 text-xs text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium no-underline">
                  Sign up here
                </Link>
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Signed up but didn't confirm your email? Try logging in - we'll help you confirm it.
              </p>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default Login;